import React, { useState, useEffect } from 'react';
import { useToast } from './Toast';
import { PageHeader, Button, Card, Badge, Modal, Input, Select, Textarea, Alert } from './ui';

const Calendar = () => {
  const toast = useToast();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [events, setEvents] = useState([]);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    title: '',
    type: 'other',
    date: '',
    time: '',
    endTime: '',
    description: '',
    assignee: '',
    reminder: 'none',
    isRecurring: false,
    recurringType: 'none',
    recurringEndDate: ''
  });
  const [filterType, setFilterType] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [ppInterviews, setPpInterviews] = useState([]);
  const [bpProspects, setBpProspects] = useState([]);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [allAssignees, setAllAssignees] = useState([]);

  // Google Calendar 連携
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEvents, setGoogleEvents] = useState([]);
  const [googleSyncing, setGoogleSyncing] = useState(false);

  // データ抽出パネル
  const [showExportPanel, setShowExportPanel] = useState(false);
  const [exportSettings, setExportSettings] = useState({
    startDate: '',
    endDate: '',
    keyword: '',
    format: 'csv',
    fields: {
      title: true,
      date: true,
      start_time: true,
      end_time: true,
      description: true,
      location: true,
      organizer: false,
      status: false,
    },
  });
  const [exportLoading, setExportLoading] = useState(false);
  const [exportPreview, setExportPreview] = useState(null); // JSON プレビュー用

  // 編集中のイベント
  const [editingEvent, setEditingEvent] = useState(null);

  // Google認証状態の確認
  useEffect(() => {
    fetch('/api/calendar/oauth/status/', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        setGoogleConnected(data.connected);
        if (data.connected) fetchGoogleEvents();
      })
      .catch(() => {});
  }, []);

  // ポップアップからのメッセージ受信（OAuth完了通知）
  useEffect(() => {
    const handler = (e) => {
      if (e.data?.type === 'GOOGLE_AUTH_SUCCESS') {
        setGoogleConnected(true);
        fetchGoogleEvents();
      } else if (e.data?.type === 'GOOGLE_AUTH_ERROR') {
        toast.error('Google認証に失敗しました: ' + e.data.error);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // GoogleカレンダーイベントをProdiaのフォーマットに変換
  const convertGoogleEvent = (gEvent) => {
    const start = gEvent.start?.dateTime || gEvent.start?.date || '';
    const date = start.substring(0, 10);
    const time = start.length > 10 ? start.substring(11, 16) : '';
    const endRaw = gEvent.end?.dateTime || gEvent.end?.date || '';
    const endTime = endRaw.length > 10 ? endRaw.substring(11, 16) : '';
    return {
      id: `google-${gEvent.id}`,
      googleId: gEvent.id,
      title: gEvent.summary || '（タイトルなし）',
      type: 'google',
      date,
      time,
      endTime,
      description: gEvent.description || '',
      assignee: '',
      reminder: 'none',
      isRecurring: false,
    };
  };

  // Googleカレンダーのイベントを取得
  const fetchGoogleEvents = () => {
    setGoogleSyncing(true);
    fetch('/api/calendar/events/', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.events) {
          setGoogleEvents(data.events.map(convertGoogleEvent));
        }
      })
      .catch(() => toast.error('Googleカレンダーの取得に失敗しました'))
      .finally(() => setGoogleSyncing(false));
  };

  // Google連携開始
  const connectGoogle = () => {
    fetch('/api/calendar/oauth/start/', { credentials: 'include' })
      .then(res => res.json())
      .then(data => {
        if (data.auth_url) {
          window.open(data.auth_url, 'google_auth', 'width=500,height=650,left=200,top=100');
        }
      })
      .catch(() => toast.error('Google連携の開始に失敗しました'));
  };

  // Google連携解除
  const disconnectGoogle = () => {
    if (!window.confirm('Googleカレンダーの連携を解除しますか？')) return;
    fetch('/api/calendar/oauth/disconnect/', { credentials: 'include' })
      .then(() => {
        setGoogleConnected(false);
        setGoogleEvents([]);
      });
  };

  // Googleカレンダー データ抽出
  const handleExport = () => {
    const selectedFields = Object.entries(exportSettings.fields)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .join(',');

    if (!selectedFields) {
      toast.warning('出力する項目を1つ以上選択してください');
      return;
    }

    const params = new URLSearchParams();
    if (exportSettings.startDate) params.set('start_date', exportSettings.startDate);
    if (exportSettings.endDate) params.set('end_date', exportSettings.endDate);
    if (exportSettings.keyword) params.set('keyword', exportSettings.keyword);
    params.set('fields', selectedFields);
    params.set('format', exportSettings.format);

    const url = `/api/calendar/events/export/?${params.toString()}`;

    if (exportSettings.format === 'json') {
      setExportLoading(true);
      fetch(url, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          if (data.error) {
            toast.error('抽出エラー: ' + data.error);
          } else {
            setExportPreview(data);
          }
        })
        .catch(() => toast.error('データ抽出に失敗しました'))
        .finally(() => setExportLoading(false));
    } else {
      // CSVはリンクを動的に作成してダウンロード
      setExportLoading(true);
      fetch(url, { credentials: 'include' })
        .then(res => {
          if (!res.ok) return res.json().then(d => Promise.reject(d.error || '抽出失敗'));
          return res.blob();
        })
        .then(blob => {
          const blobUrl = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = blobUrl;
          const startStr = exportSettings.startDate || 'all';
          const endStr = exportSettings.endDate || 'all';
          a.download = `google_calendar_${startStr}_${endStr}.csv`;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(blobUrl);
        })
        .catch(err => toast.error('データ抽出に失敗しました: ' + err))
        .finally(() => setExportLoading(false));
    }
  };

  // PP面談データ取得
  useEffect(() => {
    fetch('/api/interviews/')
      .then(res => res.json())
      .then(data => {
        setPpInterviews(data);
        // 担当者リストを更新
        const assignees = [...new Set(data.map(d => d.sales_person))];
        setAllAssignees(prev => [...new Set([...prev, ...assignees])]);
      })
      .catch(err => console.error('PP面談データ取得エラー:', err));
  }, []);

  // BP商談データ取得
  useEffect(() => {
    fetch('/api/bp-prospects/')
      .then(res => res.json())
      .then(data => {
        setBpProspects(data);
        // 担当者リストを更新
        const assignees = [...new Set(data.map(d => d.main_planner))];
        setAllAssignees(prev => [...new Set([...prev, ...assignees])]);
      })
      .catch(err => console.error('BP商談データ取得エラー:', err));
  }, []);

  // カスタムイベントデータ取得
  const fetchCustomEvents = () => {
    fetch('/api/calendar-events/')
      .then(res => res.json())
      .then(data => {
        // APIのスネークケースフィールドをフロント用に変換
        const mapped = data.map(e => ({
          ...e,
          id: `custom-${e.id}`,
          _dbId: e.id,
          endTime: e.end_time,
          isRecurring: e.is_recurring,
          recurringType: e.recurring_type,
          recurringEndDate: e.recurring_end_date,
        }));
        setEvents(mapped);
        const assignees = [...new Set(mapped.map(e => e.assignee).filter(Boolean))];
        setAllAssignees(prev => [...new Set([...prev, ...assignees])]);
      })
      .catch(err => console.error('カスタムイベント取得エラー:', err));
  };

  useEffect(() => {
    fetchCustomEvents();
  }, []);

  // リマインダーチェック
  useEffect(() => {
    const checkReminders = () => {
      const now = new Date();
      events.forEach(event => {
        if (event.reminder !== 'none' && event.date && event.time) {
          const eventDate = new Date(`${event.date}T${event.time}`);
          let reminderTime;
          
          switch (event.reminder) {
            case '5min':
              reminderTime = new Date(eventDate.getTime() - 5 * 60000);
              break;
            case '15min':
              reminderTime = new Date(eventDate.getTime() - 15 * 60000);
              break;
            case '30min':
              reminderTime = new Date(eventDate.getTime() - 30 * 60000);
              break;
            case '1hour':
              reminderTime = new Date(eventDate.getTime() - 60 * 60000);
              break;
            case '1day':
              reminderTime = new Date(eventDate.getTime() - 24 * 60 * 60000);
              break;
            default:
              return;
          }
          
          // リマインダー時刻の1分以内なら通知
          if (Math.abs(now - reminderTime) < 60000) {
            if (Notification.permission === 'granted') {
              new Notification(`予定のリマインダー: ${event.title}`, {
                body: `${event.date} ${event.time}`,
                icon: '/favicon.ico'
              });
            }
          }
        }
      });
    };

    // 通知の許可を要求
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }

    const interval = setInterval(checkReminders, 60000); // 1分ごとにチェック
    return () => clearInterval(interval);
  }, [events]);

  // イベント保存（互換性のため残すが内部では不使用）
  const saveEvents = (updatedEvents) => {
    setEvents(updatedEvents);
  };

  // 定期予定を展開
  const expandRecurringEvents = (event, startDate, endDate) => {
    if (!event.isRecurring || event.recurringType === 'none') {
      return [event];
    }

    const expandedEvents = [];
    const eventDate = new Date(event.date);
    const recurringEnd = event.recurringEndDate ? new Date(event.recurringEndDate) : endDate;
    
    let currentDate = new Date(eventDate);
    
    while (currentDate <= recurringEnd && currentDate <= endDate) {
      if (currentDate >= startDate) {
        expandedEvents.push({
          ...event,
          date: formatDate(currentDate),
          id: `${event.id}-${formatDate(currentDate)}`
        });
      }
      
      switch (event.recurringType) {
        case 'daily':
          currentDate.setDate(currentDate.getDate() + 1);
          break;
        case 'weekly':
          currentDate.setDate(currentDate.getDate() + 7);
          break;
        case 'monthly':
          currentDate.setMonth(currentDate.getMonth() + 1);
          break;
        default:
          return expandedEvents;
      }
    }
    
    return expandedEvents;
  };

  // 週の日数を取得
  const getDaysInWeek = (date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day); // 日曜日から開始
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const currentDay = new Date(startOfWeek);
      currentDay.setDate(startOfWeek.getDate() + i);
      days.push(currentDay);
    }
    return days;
  };

  // 月の日数を取得
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    const days = [];
    // 前月の日付で埋める
    for (let i = 0; i < firstDayOfMonth; i++) {
      const prevMonthDay = new Date(year, month, -firstDayOfMonth + i + 1);
      days.push({ date: prevMonthDay, isCurrentMonth: false });
    }
    
    // 当月の日付
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // 次月の日付で埋める(6週間分)
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  // 日付の予定を取得
  const getEventsForDate = (date) => {
    const dateStr = formatDate(date);
    const allEvents = [];

    // PP面談
    if (filterType === 'all' || filterType === 'pp') {
      ppInterviews
        .filter(interview => interview.interview_date === dateStr)
        .filter(interview => filterAssignee === 'all' || interview.sales_person === filterAssignee)
        .forEach(interview => {
          allEvents.push({
            id: `pp-${interview.id}`,
            title: `【PP】${interview.company_name}`,
            type: 'pp',
            time: interview.interview_time || '未定',
            data: interview,
            assignee: interview.sales_person,
            linkedTo: 'pp',
            linkedId: interview.id
          });
        });
    }

    // BP商談
    if (filterType === 'all' || filterType === 'bp') {
      bpProspects
        .filter(prospect => prospect.interview_date === dateStr)
        .filter(prospect => filterAssignee === 'all' || prospect.main_planner === filterAssignee)
        .forEach(prospect => {
          allEvents.push({
            id: `bp-${prospect.id}`,
            title: `【BP】${prospect.company_name}`,
            type: 'bp',
            time: prospect.interview_time || '未定',
            data: prospect,
            assignee: prospect.main_planner,
            linkedTo: 'bp',
            linkedId: prospect.id
          });
        });
    }

    // Googleカレンダーイベント
    if (googleConnected && (filterType === 'all' || filterType === 'google')) {
      googleEvents
        .filter(e => e.date === dateStr)
        .forEach(e => allEvents.push(e));
    }

    // カスタムイベント（定期予定を含む）
    if (filterType === 'all' || filterType === 'other' || filterType === 'important') {
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      events.forEach(event => {
        const expandedEvents = expandRecurringEvents(event, monthStart, monthEnd);
        expandedEvents
          .filter(e => e.date === dateStr)
          .filter(e => filterType === 'all' || e.type === filterType)
          .filter(e => filterAssignee === 'all' || e.assignee === filterAssignee)
          .forEach(e => {
            allEvents.push(e);
          });
      });
    }

    return allEvents.sort((a, b) => {
      const timeA = a.time || '00:00';
      const timeB = b.time || '00:00';
      return timeA.localeCompare(timeB);
    });
  };

  // 日付フォーマット
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 時刻フォーマット (HH:MM形式を時間数値に変換)
  const timeToNumber = (timeStr) => {
    if (!timeStr || timeStr === '未定') return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours + minutes / 60;
  };

  // 表示期間を変更
  const changeDate = (delta) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(newDate.getMonth() + delta);
    } else if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (delta * 7));
    } else {
      newDate.setDate(newDate.getDate() + delta);
    }
    setCurrentDate(newDate);
  };

  // 今日に戻る
  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // イベント追加・更新共通のフォームリセット
  const resetEventForm = () => {
    setNewEvent({
      title: '', type: 'other', date: '', time: '', endTime: '',
      description: '', assignee: '', reminder: 'none',
      isRecurring: false, recurringType: 'none', recurringEndDate: ''
    });
    setEditingEvent(null);
    setShowEventModal(false);
  };

  // イベント追加
  const handleAddEvent = () => {
    if (!newEvent.title || !newEvent.date) {
      toast.warning('タイトルと日付は必須です');
      return;
    }

    // 編集モード
    if (editingEvent) {
      handleUpdateEvent();
      return;
    }

    if (googleConnected) {
      // Google連携中はGoogleカレンダーのみに保存
      fetch('/api/calendar/events/create/', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEvent),
      })
        .then(res => res.json())
        .then(data => {
          if (data.event) {
            setGoogleEvents(prev => [...prev, convertGoogleEvent(data.event)]);
          }
        })
        .catch(() => {
          // Googleへの追加失敗時はDBに保存
          fetch('/api/calendar-events/', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              title: newEvent.title, type: newEvent.type, date: newEvent.date,
              time: newEvent.time, end_time: newEvent.endTime,
              description: newEvent.description, assignee: newEvent.assignee,
              reminder: newEvent.reminder, is_recurring: newEvent.isRecurring,
              recurring_type: newEvent.recurringType, recurring_end_date: newEvent.recurringEndDate,
            }),
          })
            .then(r => r.json())
            .then(() => fetchCustomEvents())
            .catch(() => {});
          toast.warning('Googleカレンダーへの追加に失敗したため、Prodia内に保存しました');
        });
    } else {
      // Google未連携時はDBに保存
      fetch('/api/calendar-events/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newEvent.title, type: newEvent.type, date: newEvent.date,
          time: newEvent.time, end_time: newEvent.endTime,
          description: newEvent.description, assignee: newEvent.assignee,
          reminder: newEvent.reminder, is_recurring: newEvent.isRecurring,
          recurring_type: newEvent.recurringType, recurring_end_date: newEvent.recurringEndDate,
        }),
      })
        .then(r => r.json())
        .then(() => fetchCustomEvents())
        .catch(() => toast.error('イベントの保存に失敗しました'));
    }

    // プランナーリストを更新
    if (newEvent.assignee && !allAssignees.includes(newEvent.assignee)) {
      setAllAssignees([...allAssignees, newEvent.assignee]);
    }

    resetEventForm();
  };

  // イベント編集モード開始
  const handleEditEvent = (event) => {
    setEditingEvent(event);
    setNewEvent({
      title: event.title || '',
      type: event.type === 'google' ? 'other' : (event.type || 'other'),
      date: event.date || '',
      time: event.time || '',
      endTime: event.endTime || '',
      description: event.description || '',
      assignee: event.assignee || '',
      reminder: event.reminder || 'none',
      isRecurring: event.isRecurring || false,
      recurringType: event.recurringType || 'none',
      recurringEndDate: event.recurringEndDate || '',
    });
    setShowDetailModal(false);
    setShowEventModal(true);
  };

  // イベント更新
  const handleUpdateEvent = () => {
    if (!editingEvent) return;

    if (editingEvent.id.startsWith('google-')) {
      const googleId = editingEvent.googleId;
      fetch(`/api/calendar/events/${googleId}/update/`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newEvent),
      })
        .then(res => res.json())
        .then(data => {
          if (data.event) {
            setGoogleEvents(prev =>
              prev.map(e => e.id === editingEvent.id ? convertGoogleEvent(data.event) : e)
            );
          }
        })
        .catch(() => toast.error('Google予定の更新に失敗しました'));
    } else if (editingEvent._dbId) {
      fetch(`/api/calendar-events/${editingEvent._dbId}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newEvent.title, type: newEvent.type, date: newEvent.date,
          time: newEvent.time, end_time: newEvent.endTime,
          description: newEvent.description, assignee: newEvent.assignee,
          reminder: newEvent.reminder, is_recurring: newEvent.isRecurring,
          recurring_type: newEvent.recurringType, recurring_end_date: newEvent.recurringEndDate,
        }),
      })
        .then(() => fetchCustomEvents())
        .catch(() => toast.error('予定の更新に失敗しました'));
    }

    resetEventForm();
  };

  // イベント削除
  const handleDeleteEvent = (eventId) => {
    if (eventId.startsWith('google-')) {
      const googleId = eventId.replace('google-', '');
      fetch(`/api/calendar/events/${googleId}/delete/`, {
        method: 'DELETE',
        credentials: 'include',
      })
        .then(() => {
          setGoogleEvents(prev => prev.filter(e => e.id !== eventId));
          setShowDetailModal(false);
        })
        .catch(() => toast.error('Googleカレンダーからの削除に失敗しました'));
    } else if (eventId.startsWith('custom-')) {
      const evt = events.find(e => e.id === eventId);
      if (!evt?._dbId) return;
      fetch(`/api/calendar-events/${evt._dbId}/`, { method: 'DELETE' })
        .then(() => {
          fetchCustomEvents();
          setShowDetailModal(false);
        })
        .catch(() => toast.error('予定の削除に失敗しました'));
    } else {
      toast.warning('PP/BP予定は元の画面から削除してください');
    }
  };

  // ドラッグ開始
  const handleDragStart = (e, event) => {
    setDraggedEvent(event);
    e.dataTransfer.effectAllowed = 'move';
  };

  // ドラッグオーバー
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // ドロップ
  const handleDrop = (e, targetDate) => {
    e.preventDefault();
    if (!draggedEvent || !draggedEvent.id.startsWith('custom-')) {
      toast.warning('カスタムイベントのみ移動可能です');
      setDraggedEvent(null);
      return;
    }

    const updatedEvents = events.map(event => {
      if (event.id === draggedEvent.id) {
        return { ...event, date: formatDate(targetDate) };
      }
      return event;
    });

    // DB更新
    if (draggedEvent._dbId) {
      fetch(`/api/calendar-events/${draggedEvent._dbId}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: formatDate(targetDate) }),
      })
        .then(() => fetchCustomEvents())
        .catch(() => toast.error('移動の保存に失敗しました'));
    } else {
      saveEvents(updatedEvents);
    }
    setDraggedEvent(null);
  };

  // イベントタイプの色取得
  const getEventColor = (type) => {
    switch (type) {
      case 'pp':
        return 'bg-info text-white border-info-foreground';
      case 'bp':
        return 'bg-warning text-white border-warning-foreground';
      case 'important':
        return 'bg-danger text-white border-danger-foreground';
      case 'google':
        return 'bg-prodia-text-secondary text-white border-prodia-border';
      case 'other':
      default:
        return 'bg-success text-white border-success-foreground';
    }
  };

  const FILTER_TYPES = [
    { key: 'all', label: 'すべて' },
    { key: 'pp', label: 'PP' },
    { key: 'bp', label: 'BP' },
    { key: 'important', label: '重要' },
    { key: 'other', label: 'その他' },
    ...(googleConnected ? [{ key: 'google', label: 'Google' }] : []),
  ];

  const days = getDaysInMonth(currentDate);
  const weekDays = getDaysInWeek(currentDate);
  const today = new Date();
  const todayStr = formatDate(today);

  // 表示モードに応じた日付範囲テキスト
  const getDateRangeText = () => {
    if (viewMode === 'month') {
      return `${currentDate.getFullYear()}年 ${currentDate.getMonth() + 1}月`;
    } else if (viewMode === 'week') {
      const weekStart = weekDays[0];
      const weekEnd = weekDays[6];
      return `${weekStart.getMonth() + 1}/${weekStart.getDate()} - ${weekEnd.getMonth() + 1}/${weekEnd.getDate()}`;
    } else {
      return `${currentDate.getFullYear()}年 ${currentDate.getMonth() + 1}月${currentDate.getDate()}日`;
    }
  };

  return (
    <div className="flex flex-col h-full bg-prodia-background">
      <PageHeader
        title="カレンダー"
        description="スケジュール管理"
        actions={
          <>
            {googleConnected ? (
              <div className="flex items-center gap-1.5 flex-wrap">
                <Button size="sm" variant="secondary" onClick={fetchGoogleEvents} disabled={googleSyncing}>
                  <i className={`fas fa-sync-alt ${googleSyncing ? 'animate-spin' : ''}`}></i>
                  同期
                </Button>
                <Button size="sm" variant="secondary" onClick={disconnectGoogle}>
                  <img src="https://www.google.com/favicon.ico" alt="Google" className="w-3 h-3" />
                  連携中
                </Button>
                <Button size="sm" variant={showExportPanel ? 'primary' : 'secondary'} onClick={() => { setShowExportPanel(p => !p); setExportPreview(null); }}>
                  <i className="fas fa-file-export"></i>
                  データ抽出
                </Button>
              </div>
            ) : (
              <Button size="sm" variant="secondary" onClick={connectGoogle}>
                <img src="https://www.google.com/favicon.ico" alt="Google" className="w-3 h-3" />
                Googleカレンダーと連携
              </Button>
            )}
            <Button size="sm" onClick={() => { setNewEvent({ ...newEvent, date: todayStr }); setShowEventModal(true); }}>
              <i className="fas fa-plus"></i>予定を追加
            </Button>
          </>
        }
        filters={
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex bg-prodia-background border border-prodia-border rounded-lg p-0.5 gap-0.5">
                {[
                  { key: 'month', icon: 'fa-calendar', label: '月' },
                  { key: 'week', icon: 'fa-calendar-week', label: '週' },
                  { key: 'day', icon: 'fa-calendar-day', label: '日' },
                ].map(({ key, icon, label }) => (
                  <button key={key} onClick={() => setViewMode(key)} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${viewMode === key ? 'bg-prodia-surface text-prodia-primary shadow-prodia' : 'text-prodia-text-secondary hover:text-prodia-text-primary'}`}>
                    <i className={`fas ${icon} mr-1`}></i>{label}
                  </button>
                ))}
              </div>
              <Button size="sm" variant="ghost" onClick={() => changeDate(-1)}>
                <i className="fas fa-chevron-left text-xs"></i>
              </Button>
              <span className="text-sm font-bold text-prodia-text-primary min-w-[160px] text-center">{getDateRangeText()}</span>
              <Button size="sm" variant="ghost" onClick={() => changeDate(1)}>
                <i className="fas fa-chevron-right text-xs"></i>
              </Button>
              <Button size="sm" variant="secondary" onClick={goToToday}>
                <i className="fas fa-home"></i>今日
              </Button>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1 flex-wrap">
                {FILTER_TYPES.map(({ key, label }) => (
                  <button key={key} onClick={() => setFilterType(key)} className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all border ${filterType === key ? 'bg-prodia-primary text-prodia-primary-foreground border-prodia-primary' : 'bg-prodia-surface text-prodia-text-secondary border-prodia-border hover:bg-prodia-background'}`}>
                    {label}
                  </button>
                ))}
              </div>
              <select value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)} className="px-2 py-1 border border-prodia-border rounded-lg text-xs bg-prodia-surface text-prodia-text-primary focus:border-prodia-primary focus:outline-none focus:ring-2 focus:ring-prodia-primary/30">
                <option value="all">全担当者</option>
                {allAssignees.sort().map(assignee => (
                  <option key={assignee} value={assignee}>{assignee}</option>
                ))}
              </select>
            </div>
          </div>
        }
      />
      <div className="flex-1 overflow-auto px-6 py-4">

        {/* データ抽出パネル */}
        {showExportPanel && googleConnected && (
          <Card className="mb-4 bg-info-bg border-info-border" padding>
            <h3 className="text-lg font-bold text-prodia-text-primary mb-4 flex items-center gap-2">
              <i className="fas fa-file-export text-info"></i>
              Googleカレンダー データ抽出
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <Input type="date" label="抽出期間（開始）" value={exportSettings.startDate} onChange={e => setExportSettings(s => ({ ...s, startDate: e.target.value }))} />
              <Input type="date" label="抽出期間（終了）" value={exportSettings.endDate} onChange={e => setExportSettings(s => ({ ...s, endDate: e.target.value }))} />
              <div className="md:col-span-2">
                <Input type="text" label={<>キーワード絞り込み <span className="ml-2 text-xs font-normal text-prodia-text-muted">カンマ区切りでAND検索（例: 面談,田中）</span></>} value={exportSettings.keyword} onChange={e => setExportSettings(s => ({ ...s, keyword: e.target.value }))} placeholder="キーワードなしで全件抽出" />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-prodia-text-secondary mb-2">出力する項目</label>
              <div className="flex flex-wrap gap-3">
                {[
                  { key: 'title', label: 'タイトル' }, { key: 'date', label: '日付' },
                  { key: 'start_time', label: '開始時刻' }, { key: 'end_time', label: '終了時刻' },
                  { key: 'description', label: '説明' }, { key: 'location', label: '場所' },
                  { key: 'organizer', label: '主催者メール' }, { key: 'status', label: 'ステータス' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 cursor-pointer bg-prodia-surface border border-prodia-border rounded-lg px-3 py-2 text-sm hover:border-prodia-primary transition-colors">
                    <input type="checkbox" checked={exportSettings.fields[key]} onChange={e => setExportSettings(s => ({ ...s, fields: { ...s.fields, [key]: e.target.checked } }))} className="accent-prodia-primary" />
                    <span className="font-medium text-prodia-text-primary">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm font-semibold text-prodia-text-primary">出力形式:</label>
                <label className="flex items-center gap-1 cursor-pointer text-sm text-prodia-text-secondary">
                  <input type="radio" value="csv" checked={exportSettings.format === 'csv'} onChange={() => { setExportSettings(s => ({ ...s, format: 'csv' })); setExportPreview(null); }} className="accent-prodia-primary" />
                  CSV（Excel対応）
                </label>
                <label className="flex items-center gap-1 cursor-pointer text-sm text-prodia-text-secondary">
                  <input type="radio" value="json" checked={exportSettings.format === 'json'} onChange={() => { setExportSettings(s => ({ ...s, format: 'json' })); setExportPreview(null); }} className="accent-prodia-primary" />
                  JSON（プレビュー）
                </label>
              </div>
              <Button onClick={handleExport} disabled={exportLoading}>
                {exportLoading ? <><i className="fas fa-spinner animate-spin"></i> 抽出中...</> : <><i className="fas fa-download"></i> {exportSettings.format === 'csv' ? 'CSVダウンロード' : 'データ取得'}</>}
              </Button>
            </div>
            {exportPreview && (
              <div className="mt-4 bg-prodia-surface border border-prodia-border rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-prodia-text-primary"><i className="fas fa-list mr-1"></i>{exportPreview.count}件 取得</span>
                  <button onClick={() => setExportPreview(null)} className="text-prodia-text-muted hover:text-prodia-text-secondary text-xs">✕ 閉じる</button>
                </div>
                <div className="overflow-auto max-h-64 text-xs font-mono text-prodia-text-secondary bg-prodia-background rounded-lg p-3 border border-prodia-border">
                  <pre>{JSON.stringify(exportPreview.events, null, 2)}</pre>
                </div>
              </div>
            )}
          </Card>
        )}

      {/* 月表示カレンダーグリッド */}
      {viewMode === 'month' && (
        <Card className="p-6" padding={false}>
          <div className="p-6">
          {/* 曜日ヘッダー */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {['日', '月', '火', '水', '木', '金', '土'].map((day, index) => (
              <div
                key={day}
                className={`text-center font-bold py-3 rounded-lg ${
                  index === 0 ? 'text-danger bg-danger-bg' :
                  index === 6 ? 'text-info bg-info-bg' :
                  'text-prodia-text-primary bg-prodia-background'
                }`}
              >
                {day}
              </div>
            ))}
        </div>

        {/* 日付グリッド */}
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, index) => {
            const dateStr = formatDate(day.date);
            const isToday = dateStr === todayStr;
            const dayEvents = getEventsForDate(day.date);
            const isSunday = day.date.getDay() === 0;
            const isSaturday = day.date.getDay() === 6;

            return (
              <div
                key={index}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, day.date)}
                className={`min-h-[120px] border rounded-xl p-2 transition-all duration-200 ${
                  day.isCurrentMonth
                    ? isToday
                      ? 'bg-prodia-primary-subtle border-prodia-primary shadow-prodia-md'
                      : 'bg-prodia-surface border-prodia-border hover:border-prodia-primary hover:shadow-prodia'
                    : 'bg-prodia-background border-prodia-border'
                } ${draggedEvent ? 'cursor-copy' : ''}`}
              >
                {/* 日付 */}
                <div className={`text-right mb-1 font-semibold ${
                  day.isCurrentMonth
                    ? isToday
                      ? 'text-prodia-primary text-lg'
                      : isSunday
                        ? 'text-danger'
                        : isSaturday
                          ? 'text-info'
                          : 'text-prodia-text-primary'
                    : 'text-prodia-text-muted'
                }`}>
                  {day.date.getDate()}
                  {isToday && <Badge variant="primary" className="ml-1">今日</Badge>}
                </div>

                {/* イベント一覧 */}
                <div className="space-y-1">
                  {dayEvents.slice(0, 3).map(event => (
                    <div
                      key={event.id}
                      draggable={event.id.startsWith('custom-')}
                      onDragStart={(e) => handleDragStart(e, event)}
                      onClick={() => {
                        setSelectedEvent(event);
                        setShowDetailModal(true);
                      }}
                      className={`text-xs px-2 py-1 rounded border-l-4 cursor-pointer hover:shadow-md transition-all ${
                        getEventColor(event.type)
                      } text-white truncate ${
                        event.id.startsWith('custom-') ? 'cursor-move' : ''
                      }`}
                      title={event.title}
                    >
                      <div className="flex items-center gap-1">
                        <span className="text-[10px]">{event.time}</span>
                        <span className="truncate font-semibold">{event.title}</span>
                      </div>
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className="text-xs text-prodia-text-muted text-center bg-prodia-background rounded py-1">
                      +{dayEvents.length - 3}件
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
          </div>
        </Card>
      )}

      {/* 週表示 */}
      {viewMode === 'week' && (
        <Card className="p-6" padding={false}>
          <div className="p-6">
          {/* 時間軸付きグリッド */}
          <div className="flex">
            {/* 時刻列 */}
            <div className="w-20 flex-shrink-0">
              <div className="h-16"></div>
              {Array.from({ length: 24 }, (_, hour) => (
                <div key={hour} className="h-16 border-t border-prodia-border pt-1 pr-2 text-right text-xs text-prodia-text-muted">
                  {`${hour}:00`}
                </div>
              ))}
            </div>

            {/* 日付列 */}
            <div className="flex-1 grid grid-cols-7 gap-1">
              {weekDays.map((day, dayIndex) => {
                const dateStr = formatDate(day);
                const isToday = dateStr === todayStr;
                const dayEvents = getEventsForDate(day);
                const isSunday = day.getDay() === 0;
                const isSaturday = day.getDay() === 6;

                return (
                  <div key={dayIndex} className="relative">
                    {/* 日付ヘッダー */}
                    <div className={`h-16 flex flex-col items-center justify-center border rounded-xl mb-1 ${
                      isToday 
                        ? 'bg-prodia-primary text-prodia-primary-foreground border-prodia-primary shadow-prodia-md' 
                        : 'bg-prodia-background border-prodia-border'
                    }`}>
                      <div className={`text-xs ${
                        isToday ? 'text-prodia-primary-foreground' : 
                        isSunday ? 'text-danger' : 
                        isSaturday ? 'text-info' : 
                        'text-prodia-text-secondary'
                      }`}>
                        {['日', '月', '火', '水', '木', '金', '土'][day.getDay()]}
                      </div>
                      <div className={`text-lg font-bold ${
                        isToday ? 'text-prodia-primary-foreground' : 'text-prodia-text-primary'
                      }`}>
                        {day.getDate()}
                      </div>
                    </div>

                    {/* タイムグリッド */}
                    <div 
                      className="relative"
                      onDragOver={handleDragOver}
                      onDrop={(e) => handleDrop(e, day)}
                    >
                      {Array.from({ length: 24 }, (_, hour) => (
                        <div 
                          key={hour} 
                          className="h-16 border-t border-prodia-border hover:bg-prodia-primary-subtle/50 transition-colors"
                        ></div>
                      ))}

                      {/* イベント配置 */}
                      {dayEvents.map(event => {
                        const startHour = timeToNumber(event.time);
                        const endHour = event.endTime ? timeToNumber(event.endTime) : startHour + 1;
                        const top = startHour * 64; // 64px per hour
                        const height = (endHour - startHour) * 64;

                        return (
                          <div
                            key={event.id}
                            draggable={event.id.startsWith('custom-')}
                            onDragStart={(e) => handleDragStart(e, event)}
                            onClick={() => {
                              setSelectedEvent(event);
                              setShowDetailModal(true);
                            }}
                            className={`absolute left-0 right-0 mx-1 rounded-lg border-l-4 p-2 cursor-pointer hover:shadow-lg transition-all z-10 ${
                              getEventColor(event.type)
                            } text-white overflow-hidden ${
                              event.id.startsWith('custom-') ? 'cursor-move' : ''
                            }`}
                            style={{ 
                              top: `${top}px`, 
                              height: `${Math.max(height, 32)}px`,
                              minHeight: '32px'
                            }}
                          >
                            <div className="text-xs font-bold truncate">{event.time}</div>
                            <div className="text-sm font-semibold truncate">{event.title}</div>
                            {event.assignee && (
                              <div className="text-xs opacity-90 truncate mt-1">
                                <i className="fas fa-user mr-1"></i>{event.assignee}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          </div>
        </Card>
      )}

      {/* 日表示 */}
      {viewMode === 'day' && (
        <Card className="p-6" padding={false}>
          <div className="p-6">
          <div className="flex gap-6">
            {/* 時刻列 */}
            <div className="w-24 flex-shrink-0">
              {Array.from({ length: 24 }, (_, hour) => (
                <div key={hour} className="h-20 border-t border-prodia-border pt-2 pr-3 text-right">
                  <div className="text-sm font-semibold text-prodia-text-primary">{`${hour}:00`}</div>
                </div>
              ))}
            </div>

            {/* イベント列 */}
            <div className="flex-1 relative">
              {Array.from({ length: 24 }, (_, hour) => (
                <div 
                  key={hour} 
                  className="h-20 border-t border-prodia-border hover:bg-prodia-primary-subtle/50 transition-colors cursor-pointer"
                  onClick={() => {
                    const hourStr = String(hour).padStart(2, '0');
                    setNewEvent({ ...newEvent, date: todayStr, time: `${hourStr}:00` });
                    setShowEventModal(true);
                  }}
                ></div>
              ))}

              {/* イベント配置 */}
              {getEventsForDate(currentDate).map(event => {
                const startHour = timeToNumber(event.time);
                const endHour = event.endTime ? timeToNumber(event.endTime) : startHour + 1;
                const top = startHour * 80; // 80px per hour
                const height = (endHour - startHour) * 80;

                return (
                  <div
                    key={event.id}
                    draggable={event.id.startsWith('custom-')}
                    onDragStart={(e) => handleDragStart(e, event)}
                    onClick={() => {
                      setSelectedEvent(event);
                      setShowDetailModal(true);
                    }}
                    className={`absolute left-0 right-0 rounded-xl border-l-4 p-4 cursor-pointer hover:shadow-xl transition-all z-10 ${
                      getEventColor(event.type)
                    } text-white ${
                      event.id.startsWith('custom-') ? 'cursor-move' : ''
                    }`}
                    style={{ 
                      top: `${top}px`, 
                      height: `${Math.max(height, 60)}px`,
                      minHeight: '60px'
                    }}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="text-lg font-bold">{event.time}</div>
                      {event.endTime && (
                        <div className="text-sm opacity-90">- {event.endTime}</div>
                      )}
                    </div>
                    <div className="text-xl font-bold mb-2">{event.title}</div>
                    {event.assignee && (
                      <div className="flex items-center gap-2 text-sm opacity-90">
                        <i className="fas fa-user"></i>
                        <span>{event.assignee}</span>
                      </div>
                    )}
                    {event.description && height > 100 && (
                      <div className="text-sm opacity-90 mt-2 line-clamp-2">
                        {event.description}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* 現在時刻インジケーター（今日の場合） */}
              {formatDate(currentDate) === todayStr && (
                <div 
                  className="absolute left-0 right-0 border-t-2 border-danger z-20"
                  style={{ top: `${new Date().getHours() * 80 + (new Date().getMinutes() / 60) * 80}px` }}
                >
                  <div className="w-3 h-3 bg-danger rounded-full -mt-1.5 -ml-1.5"></div>
                </div>
              )}
            </div>
          </div>
          </div>
        </Card>
      )}

      {/* 新規イベント追加モーダル */}
      <Modal
        open={showEventModal}
        onClose={() => setShowEventModal(false)}
        title={editingEvent ? '予定を編集' : '新しい予定を追加'}
        hideFooter
        size="md"
      >
        <div className="space-y-4">
          <Input label="タイトル *" type="text" value={newEvent.title} onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })} placeholder="予定のタイトル" />
          <Select label="種類" value={newEvent.type} onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}>
            <option value="other">その他</option>
            <option value="important">重要</option>
          </Select>
          <Input label="日付 *" type="date" value={newEvent.date} onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="開始時刻" type="time" value={newEvent.time} onChange={(e) => setNewEvent({ ...newEvent, time: e.target.value })} />
            <Input label="終了時刻" type="time" value={newEvent.endTime} onChange={(e) => setNewEvent({ ...newEvent, endTime: e.target.value })} />
          </div>
          <Select label="リマインダー" value={newEvent.reminder} onChange={(e) => setNewEvent({ ...newEvent, reminder: e.target.value })}>
            <option value="none">なし</option>
            <option value="5min">5分前</option>
            <option value="15min">15分前</option>
            <option value="30min">30分前</option>
            <option value="1hour">1時間前</option>
            <option value="1day">1日前</option>
          </Select>
          <div>
            <label className="flex items-center gap-2 mb-3">
              <input type="checkbox" checked={newEvent.isRecurring} onChange={(e) => setNewEvent({ ...newEvent, isRecurring: e.target.checked, recurringType: e.target.checked ? 'weekly' : 'none' })} className="w-5 h-5 text-prodia-primary rounded accent-prodia-primary" />
              <span className="text-sm font-semibold text-prodia-text-primary">
                <i className="fas fa-redo mr-2 text-prodia-primary"></i>定期予定
              </span>
            </label>
            {newEvent.isRecurring && (
              <div className="grid grid-cols-2 gap-4 ml-7">
                <Select label="繰り返し" value={newEvent.recurringType} onChange={(e) => setNewEvent({ ...newEvent, recurringType: e.target.value })}>
                  <option value="daily">毎日</option>
                  <option value="weekly">毎週</option>
                  <option value="monthly">毎月</option>
                </Select>
                <Input label="終了日" type="date" value={newEvent.recurringEndDate} onChange={(e) => setNewEvent({ ...newEvent, recurringEndDate: e.target.value })} />
              </div>
            )}
          </div>
          <Input label="担当者" type="text" value={newEvent.assignee} onChange={(e) => setNewEvent({ ...newEvent, assignee: e.target.value })} placeholder="担当者名" />
          <Textarea label="詳細" value={newEvent.description} onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })} rows={3} placeholder="詳細説明" />
          <Button onClick={handleAddEvent} className="w-full">
            <i className={`fas ${editingEvent ? 'fa-save' : 'fa-plus'}`}></i>
            {editingEvent ? '更新' : '追加'}
          </Button>
        </div>
      </Modal>

      {/* イベント詳細モーダル */}
      <Modal
        open={showDetailModal && !!selectedEvent}
        onClose={() => setShowDetailModal(false)}
        title={selectedEvent?.title}
        hideFooter
        size="md"
      >
        {selectedEvent && (
          <div className="space-y-4">
            <p className="text-sm text-prodia-text-secondary">{selectedEvent.time}</p>

            {selectedEvent.type === 'pp' && selectedEvent.data && (
              <>
                <Card padding className="bg-info-bg border-info-border">
                  <p className="text-sm text-info-foreground mb-1">エンジニア</p>
                  <p className="font-bold text-prodia-text-primary">{selectedEvent.data.engineer_name}</p>
                </Card>
                <Card padding className="bg-info-bg border-info-border">
                  <p className="text-sm text-info-foreground mb-1">営業担当</p>
                  <p className="font-bold text-prodia-text-primary">{selectedEvent.data.sales_person}</p>
                </Card>
                {selectedEvent.data.notes && (
                  <Card padding className="bg-prodia-background">
                    <p className="text-sm text-prodia-text-secondary mb-1">備考</p>
                    <p className="text-prodia-text-primary">{selectedEvent.data.notes}</p>
                  </Card>
                )}
                <Alert variant="info" title="PP営業進捗画面で詳細を確認" />
              </>
            )}

            {selectedEvent.type === 'bp' && selectedEvent.data && (
              <>
                <Card padding className="bg-warning-bg border-warning-border">
                  <p className="text-sm text-warning-foreground mb-1">エンジニア</p>
                  <p className="font-bold text-prodia-text-primary">{selectedEvent.data.engineer_name}</p>
                </Card>
                <Card padding className="bg-warning-bg border-warning-border">
                  <p className="text-sm text-warning-foreground mb-1">メインプランナー</p>
                  <p className="font-bold text-prodia-text-primary">{selectedEvent.data.main_planner}</p>
                </Card>
                {selectedEvent.data.priority && (
                  <Card padding className="bg-prodia-background">
                    <p className="text-sm text-prodia-text-secondary mb-1">優先度</p>
                    <Badge variant={
                      selectedEvent.data.priority === '高' ? 'danger' :
                      selectedEvent.data.priority === '中' ? 'warning' : 'info'
                    }>
                      {selectedEvent.data.priority}
                    </Badge>
                  </Card>
                )}
                <Alert variant="warning" title="BP進捗画面で詳細を確認" />
              </>
            )}

            {selectedEvent.type === 'google' && (
              <>
                {selectedEvent.endTime && (
                  <Card padding className="bg-prodia-background">
                    <p className="text-sm text-prodia-text-secondary mb-1">時間</p>
                    <p className="font-bold text-prodia-text-primary">
                      {selectedEvent.time} - {selectedEvent.endTime}
                    </p>
                  </Card>
                )}
                {selectedEvent.description && (
                  <Card padding className="bg-prodia-background">
                    <p className="text-sm text-prodia-text-secondary mb-1">詳細</p>
                    <p className="text-prodia-text-primary whitespace-pre-wrap">{selectedEvent.description}</p>
                  </Card>
                )}
                <div className="flex gap-3">
                  <Button className="flex-1" onClick={() => handleEditEvent(selectedEvent)}>
                    <i className="fas fa-edit"></i>編集
                  </Button>
                  <Button className="flex-1" variant="danger" onClick={() => {
                    if (window.confirm('この予定をGoogleカレンダーから削除しますか?')) {
                      handleDeleteEvent(selectedEvent.id);
                    }
                  }}>
                    <i className="fas fa-trash"></i>削除
                  </Button>
                </div>
              </>
            )}

            {(selectedEvent.type === 'other' || selectedEvent.type === 'important') && (
              <>
                {selectedEvent.endTime && (
                  <Card padding className="bg-prodia-background">
                    <p className="text-sm text-prodia-text-secondary mb-1">時間</p>
                    <p className="font-bold text-prodia-text-primary">
                      {selectedEvent.time} - {selectedEvent.endTime}
                    </p>
                  </Card>
                )}
                {selectedEvent.assignee && (
                  <Card padding className={selectedEvent.type === 'important' ? 'bg-danger-bg border-danger-border' : 'bg-success-bg border-success-border'}>
                    <p className="text-sm text-prodia-text-secondary mb-1">担当者</p>
                    <p className="font-bold text-prodia-text-primary">{selectedEvent.assignee}</p>
                  </Card>
                )}
                {selectedEvent.reminder && selectedEvent.reminder !== 'none' && (
                  <Card padding className="bg-warning-bg border-warning-border">
                    <p className="text-sm text-warning-foreground mb-1">
                      <i className="fas fa-bell mr-2"></i>リマインダー
                    </p>
                    <p className="font-bold text-prodia-text-primary">
                      {selectedEvent.reminder === '5min' ? '5分前' :
                       selectedEvent.reminder === '15min' ? '15分前' :
                       selectedEvent.reminder === '30min' ? '30分前' :
                       selectedEvent.reminder === '1hour' ? '1時間前' :
                       selectedEvent.reminder === '1day' ? '1日前' : ''}
                    </p>
                  </Card>
                )}
                {selectedEvent.isRecurring && (
                  <Card padding className="bg-success-bg border-success-border">
                    <p className="text-sm text-success-foreground mb-1">
                      <i className="fas fa-redo mr-2"></i>定期予定
                    </p>
                    <p className="font-bold text-prodia-text-primary">
                      {selectedEvent.recurringType === 'daily' ? '毎日' :
                       selectedEvent.recurringType === 'weekly' ? '毎週' :
                       selectedEvent.recurringType === 'monthly' ? '毎月' : ''}
                      {selectedEvent.recurringEndDate && ` (${selectedEvent.recurringEndDate}まで)`}
                    </p>
                  </Card>
                )}
                {selectedEvent.description && (
                  <Card padding className="bg-prodia-background">
                    <p className="text-sm text-prodia-text-secondary mb-1">詳細</p>
                    <p className="text-prodia-text-primary whitespace-pre-wrap">{selectedEvent.description}</p>
                  </Card>
                )}
                <div className="flex gap-3">
                  <Button className="flex-1" onClick={() => handleEditEvent(selectedEvent)}>
                    <i className="fas fa-edit"></i>編集
                  </Button>
                  <Button className="flex-1" variant="danger" onClick={() => {
                    if (window.confirm('この予定を削除しますか?')) {
                      handleDeleteEvent(selectedEvent.id);
                    }
                  }}>
                    <i className="fas fa-trash"></i>削除
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </Modal>
      </div>
    </div>
  );
};

export default Calendar;
